<template>
  <div>
    <section v-if="mode === 'resetPassword'">
      <h1>{{ $t("passwordReset") }}</h1>

      <Spinner v-if="resetPassword.checking" />

      <div v-else-if="resetPassword.checkingError" class="alert alert-danger">
        <span v-if="resetPassword.checkingError === 'auth/invalid-action-code'">
          {{ $t("wrongVerifyCode") }}
          <router-link
            :to="{ name: 'account.passwordReset' }"
            class="text-white"
            >{{ $t("resendPasswordResetMail") }}</router-link
          >
        </span>

        <span v-else>
          {{ $t("errorUnknown") }} ({{ resetPassword.checkingError }})
        </span>
      </div>

      <div v-show="!resetPassword.checkingError">
        <div class="col-12 col-md-6">
          <div class="alert alert-success mb-4" v-if="resetPassword.success">
            {{ $t("changePasswordFormSuccess") }}
            <router-link :to="{ name: 'account.login' }" class="text-white">{{
              $t("goToLogin")
            }}</router-link>
          </div>

          <div class="alert alert-danger mb-4" v-else-if="resetPassword.error">
            {{ resetPassword.error }}
          </div>

          <form action="#" @submit.prevent="resetPasswordAction" id="form">
            <div class="form-floating mb-4">
              <input
                id="password"
                class="form-control"
                :placeholder="$t('changePasswordFormNewPassword')"
                :type="passwordType"
                v-model="resetPassword.password"
                minlength="6"
                required
              />
              <label for="password">
                {{ $t("changePasswordFormNewPassword") }}
              </label>
            </div>

            <div class="form-floating mb-4">
              <input
                id="passwordRepeat"
                class="form-control"
                :placeholder="$t('changePasswordFormNewPasswordRepeat')"
                :type="passwordType"
                v-model="resetPassword.passwordRepeat"
                minlength="6"
                required
              />
              <label for="passwordRepeat">
                {{ $t("changePasswordFormNewPasswordRepeat") }}
              </label>
            </div>

            <button
              type="submit"
              class="btn btn-secondary w-100"
              :disabled="resetPasswordButtonDisabled"
            >
              <span v-if="!resetPassword.loading">{{
                $t("passwordReset")
              }}</span>
              <Spinner v-else centered />
            </button>
          </form>
        </div>
      </div>
    </section>

    <section v-else-if="mode === 'recoverEmail'">
      <h1>E-Mail-Adresse vergessen</h1>
    </section>

    <section v-else-if="mode === 'verifyEmail'">
      <h1>{{ $t("verifyEmail") }}</h1>

      <div
        v-if="verifyEmail.loaded && verifyEmail.success"
        class="alert alert-success"
      >
        {{ $t("verifyEmailSuccess") }}
        <router-link
          :to="{ name: 'account.login' }"
          class="text-white"
          v-if="!$store.getters['user/isLoggedIn']"
          >{{ $t("login") }}</router-link
        >
        <router-link
          :to="{ name: 'account.login' }"
          class="text-white"
          v-else
          >{{ $t("viewProfile") }}</router-link
        >
      </div>

      <div
        v-else-if="verifyEmail.loaded && !verifyEmail.success"
        class="alert alert-danger"
      >
        <span v-if="verifyEmail.errorCode === 'auth/invalid-action-code'">
          {{ $t("wrongVerifyCode") }}
          <router-link :to="{ name: 'account.login' }" class="text-white">{{
            $t("loginRetry")
          }}</router-link>
        </span>

        <span v-else>
          {{ $t("errorUnknown") }} ({{ verifyEmail.errorCode }})
        </span>
      </div>

      <spinner v-else />
    </section>

    <section v-else>
      <div class="alert alert-danger">
        {{ $t("errorUnknown") }}
      </div>
    </section>
  </div>
</template>

<script>
import Spinner from "@/components/Spinner";

import auth from "@/firebase/auth";
import {
  applyActionCode,
  checkActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
  signInWithEmailAndPassword,
} from "firebase/auth";

export default {
  name: "EmailAction",
  components: { Spinner },

  data() {
    return {
      resetPassword: {
        loading: false,
        success: false,
        error: null,
        password: null,
        showPassword: false,
        passwordRepeat: null,
        form: null,
        formValid: false,
        checking: true,
        checkingError: false,
        email: null,
      },
      recoverEmail: false,
      verifyEmail: {
        loaded: false,
        success: false,
        errorCode: null,
      },
    };
  },
  computed: {
    mode() {
      return this.$route.query.mode || null;
    },
    actionCode() {
      return this.$route.query.oobCode || null;
    },
    continueUrl() {
      return this.$route.query.continueUrl || null;
    },
    lang() {
      return this.$route.query.lang || this.$i18n.locale;
    },
    passwordType() {
      return this.resetPassword.showPassword ? "text" : "password";
    },
    resetPasswordButtonDisabled() {
      return !this.resetPassword.formValid;
    },
  },
  watch: {
    "resetPassword.password": function () {
      this.resetPassword.formValid =
        this.resetPassword.form.checkValidity() &&
        this.resetPassword.password === this.resetPassword.passwordRepeat;
    },
    "resetPassword.passwordRepeat": function () {
      this.resetPassword.formValid =
        this.resetPassword.form.checkValidity() &&
        this.resetPassword.password === this.resetPassword.passwordRepeat;
    },
  },
  methods: {
    async handleResetPassword() {
      await verifyPasswordResetCode(auth, this.actionCode)
        .then((email) => {
          this.resetPassword.email = email;
          this.resetPassword.checking = false;
          this.resetPassword.form = document.getElementById("form");
        })
        .catch((error) => {
          this.resetPassword.checking = false;
          this.resetPassword.checkingError = error.code;
        });
    },
    async resetPasswordAction() {
      const _this = this;
      this.resetPassword.loading = true;
      await confirmPasswordReset(
        auth,
        this.actionCode,
        this.resetPassword.password
      )
        .then(async () => {
          await signInWithEmailAndPassword(
            auth,
            _this.resetPassword.email,
            _this.resetPassword.password
          );
          await this.$router.push({ name: "profile" });
        })
        .catch((error) => {
          this.resetPassword.checking = false;
          this.resetPassword.checkingError = error.code;
          this.resetPassword.loading = false;
        });
    },
    async handleRecoverEmail() {
      let restoredEmail = null;

      checkActionCode(auth, this.actionCode).then(async (info) => {
        restoredEmail = info["data"]["email"];

        auth.currentUser.email = restoredEmail;

        await applyActionCode(auth, this.actionCode);
      });
      //.catch((error) => {});
    },
    async handleVerifyEmail() {
      const _this = this;

      applyActionCode(auth, this.actionCode)
        .then(async () => {
          _this.verifyEmail.success = true;
          _this.verifyEmail.loaded = true;
          _this.$store.commit("user/SET_USER", { emailVerified: true });
          await _this.$store.dispatch("authInit");
        })
        .catch((error) => {
          _this.verifyEmail.loaded = true;
          _this.verifyEmail.errorCode = error.code;
        });
    },
  },
  async mounted() {
    if (!this.mode && this.$store.getters["user/isLoggedIn"]) {
      await this.$router.push({ name: "profile" });
    } else if (!this.mode && !this.$store.getters["user/isLoggedIn"]) {
      await this.$router.push({ name: "account.login" });
    }

    switch (this.mode) {
      case "resetPassword":
        await this.handleResetPassword();
        break;

      case "recoverEmail":
        await this.handleRecoverEmail();
        break;

      case "verifyEmail":
        await this.handleVerifyEmail();
        break;
    }
  },
};
</script>

<style scoped></style>
